import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from './Logo';
import { CONTACT } from '../mock';

const LAST_UPDATED = 'February 2026';
const COMPANY = 'Absolute Marine Equipment';
const LEGAL_ENTITY = 'Group Absolute Builders LLC';
const MAILING_ADDRESS = '2670 Colonial Blvd #2067, Fort Myers, FL 33907';

export default function SmsTerms() {
  useEffect(() => {
    const company = COMPANY;
    document.title = `SMS Terms — ${company}`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-black min-h-screen text-zinc-200">
      <header className="border-b border-white/5 bg-black/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Logo />
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="sms-back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mb-10 sm:mb-14">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-cyan-300 font-semibold">
            Legal
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            SMS &amp; Messaging Terms
          </h1>
          <p className="mt-3 text-sm text-zinc-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <div className="space-y-10 leading-relaxed text-[15px] sm:text-base text-zinc-300">
          <section>
            <p>
              These SMS &amp; Messaging Terms govern how {COMPANY} (a d/b/a
              of {LEGAL_ENTITY}) sends and receives text (SMS/MMS) messages
              from you. By providing your mobile phone number to {COMPANY}{' '}
              — through our website contact form, in person, by phone, or by
              any other means — and agreeing to receive text messages, you
              accept these terms.
            </p>
          </section>

          <Section title="1. Program description">
            <p>
              <strong className="text-white">Program name:</strong>{' '}
              {COMPANY} Quote &amp; Customer Care
            </p>
            <p className="mt-3">
              We use SMS to communicate with customers and prospective
              customers about their inquiries, quotes, orders, deliveries,
              service, and — for customers who opt in — occasional new
              product announcements. Message content is transactional and
              conversational, tailored to your inquiry with us.
            </p>
          </Section>

          <Section title="2. Consent (opt-in)">
            <p>
              You opt in to receive SMS messages from {COMPANY} when you
              affirmatively agree to our SMS terms by (a) checking the SMS
              consent box on our website contact form, (b) texting us first
              from your mobile phone, or (c) providing verbal or written
              consent to a member of our team. Your consent is not required
              as a condition of purchasing any goods or services. Message
              and data rates may apply.
            </p>
          </Section>

          <Section title="3. Message types you may receive">
            <ul className="mt-1 space-y-1.5 pl-5 list-disc marker:text-cyan-400/60">
              <li>Replies to your quote request</li>
              <li>Follow-up when we can’t reach you by phone</li>
              <li>Order confirmations, invoices, and payment reminders</li>
              <li>Manufacturing progress updates and delivery ETAs</li>
              <li>Warranty and service follow-ups</li>
              <li>Occasional new-product or promotional notices (opt-out any time)</li>
            </ul>
          </Section>

          <Section title="4. Message frequency">
            <p>
              Message frequency varies and depends on how actively you are
              engaging with us. Most customers receive fewer than 10 messages
              per month during an active quote or delivery window, and very
              few messages outside of that.
            </p>
          </Section>

          <Section title="5. Opt out (STOP)">
            <p>
              You can cancel the SMS service at any time by replying{' '}
              <strong className="text-white">STOP</strong> to any message
              from us. After you send us <strong className="text-white">
                STOP
              </strong>
              , we will send you one final message confirming you have been
              unsubscribed. You will not receive additional SMS messages from
              this program after that. If you want to re-join, sign up as you
              did the first time or text{' '}
              <strong className="text-white">START</strong>.
            </p>
          </Section>

          <Section title="6. Help (HELP)">
            <p>
              For help with our SMS program, reply{' '}
              <strong className="text-white">HELP</strong> to any message from
              us, or contact us using the details in Section 10 below.
            </p>
          </Section>

          <Section title="7. Costs">
            <p>
              <strong className="text-white">
                Message and data rates may apply.
              </strong>{' '}
              Depending on your mobile plan, standard SMS and MMS rates from
              your carrier may apply to each message sent or received from
              this program. {COMPANY} does not charge for the messages
              themselves. Contact your carrier for details of your text and
              data plan.
            </p>
          </Section>

          <Section title="8. Supported carriers">
            <p>
              Our SMS program is compatible with U.S.-based mobile carriers
              including AT&amp;T, Verizon, T-Mobile (including former Sprint,
              Boost, and Metro numbers), U.S. Cellular, and most regional
              carriers.{' '}
              <strong className="text-white">
                Carriers are not liable for delayed or undelivered messages.
              </strong>
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              We do not sell, rent, or share your mobile phone number or SMS
              consent data with third parties or affiliates for their
              marketing or promotional purposes. Phone numbers collected for
              SMS purposes are used only by {COMPANY} to send you the messages
              described above, and by our SMS service provider to route and
              deliver those messages. See our{' '}
              <Link to="/privacy" className="text-cyan-300 hover:text-cyan-200">
                Privacy Policy
              </Link>{' '}
              for full details on how we handle personal information.
            </p>
          </Section>

          <Section title="10. Contact us">
            <p>Questions about this SMS program?</p>
            <ul className="mt-3 space-y-1.5 pl-5 list-none">
              <li>
                <span className="text-zinc-500">Email:</span>{' '}
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="text-cyan-300 hover:text-cyan-200"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Phone:</span>{' '}
                <a
                  href={`tel:${CONTACT.salesPhone.tel}`}
                  className="text-cyan-300 hover:text-cyan-200"
                >
                  {CONTACT.salesPhone.number}
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Mail:</span>{' '}
                {LEGAL_ENTITY} d/b/a {COMPANY}, {MAILING_ADDRESS}
              </li>
            </ul>
          </Section>

          <Section title="11. Changes to these terms">
            <p>
              We may update these SMS Terms from time to time. Changes take
              effect on the “Last updated” date shown at the top of this
              page. Continued participation in the program after changes are
              posted constitutes acceptance of the updated terms.
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} {COMPANY}. All rights reserved.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-white text-lg sm:text-xl font-semibold mb-3">
        {title}
      </h2>
      <div className="text-zinc-300 text-[15px] sm:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}
